import React, { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    TablePagination,
    Tooltip,
} from "@mui/material";
import { UploadFile, Search, Edit, Delete, Close, Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import hsCodeService from "../../services/hsCodeService";
import EmptyState from "../common/EmptyState";
import SkeletonLoader from "../common/SkeletonLoader";
import ConfirmDialog from "../common/ConfirmDialog";

const HsCodesPanel = () => {
    const { enqueueSnackbar } = useSnackbar();

    const [codes, setCodes] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async (overrides = {}) => {
        setLoading(true);
        try {
            const { data } = await hsCodeService.getAll({
                assignableOnly: true,
                search: search || undefined,
                page: page + 1,
                limit: rowsPerPage,
                ...overrides,
            });
            setCodes(data.data || []);
            setTotal(data.pagination?.total || 0);
        } catch {
            enqueueSnackbar("Failed to load HS codes", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [page, rowsPerPage]);

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setPage(0);
            load({ page: 1, search: e.target.value });
        }
    };

    const onDropCsv = useCallback(async (accepted) => {
        if (!accepted[0]) return;
        setUploading(true);
        setUploadResult(null);
        try {
            const { data } = await hsCodeService.bulkUpload(accepted[0]);
            setUploadResult({ success: true, message: data.message });
            enqueueSnackbar(data.message, { variant: "success" });
            setPage(0);
            load({ page: 1 });
        } catch (err) {
            const message = err.response?.data?.message || "Failed to upload CSV";
            setUploadResult({ success: false, message });
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setUploading(false);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: onDropCsv,
        accept: { "text/csv": [".csv"] },
        maxFiles: 1,
        maxSize: 5 * 1024 * 1024,
    });

    const openEdit = (code) => {
        setEditTarget(code);
        setEditForm({
            description: code.description,
            dutyRate: code.dutyRate || "",
            dutyMethod: code.dutyMethod || "",
            unitOfMeasure: code.unitOfMeasure || "",
        });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await hsCodeService.update(editTarget._id, editForm);
            enqueueSnackbar("HS code updated", { variant: "success" });
            setEditTarget(null);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to update HS code", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await hsCodeService.delete(deleteTarget._id);
            enqueueSnackbar("HS code deleted", { variant: "success" });
            setDeleteTarget(null);
            load();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to delete HS code", { variant: "error" });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Upload card */}
            <Card>
                <CardContent>
                    <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                        Bulk Import
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Upload a CSV to replace the entire HS code list. Expected columns: ID, Code, Code WO Section,
                        Unit of Measure, Duty Rate, Duty Method, Description, Parent ID, Depth.
                    </Typography>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: "2px dashed",
                            borderColor: isDragActive ? "primary.main" : "divider",
                            borderRadius: 2,
                            p: 3,
                            textAlign: "center",
                            cursor: "pointer",
                            bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadFile sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {uploading ? "Uploading & processing…" : "Drag & drop a CSV file here, or click to browse"}
                        </Typography>
                    </Box>

                    {uploading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}

                    {uploadResult && (
                        <Alert severity={uploadResult.success ? "success" : "error"} sx={{ mt: 2, borderRadius: 2 }}>
                            {uploadResult.message}
                        </Alert>
                    )}

                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                        Uploading a new CSV replaces the entire existing HS code list.
                    </Alert>
                </CardContent>
            </Card>

            {/* Browse/edit table */}
            <Card>
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <TextField
                        size="small"
                        placeholder="Search code or description…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearch}
                        sx={{ width: { xs: "100%", sm: 320 } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>

                {loading ? (
                    <SkeletonLoader rows={8} />
                ) : codes.length === 0 ? (
                    <EmptyState
                        icon="📋"
                        title="No HS codes found"
                        description="Upload a CSV above to import your HS code list."
                    />
                ) : (
                    <>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell>Duty</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {codes.map((c) => (
                                        <TableRow key={c._id} hover>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>
                                                {c.codeWoSection || c.code}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={c.fullPath}>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
                                                        {c.description}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{c.unitOfMeasure || "—"}</TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                {c.dutyRate ? `${c.dutyRate}${c.dutyMethod || ""}` : "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => openEdit(c)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(+e.target.value);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[20, 50, 100]}
                            sx={{ borderTop: "1px solid", borderColor: "divider" }}
                        />
                    </>
                )}
            </Card>

            {/* Edit dialog */}
            <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight={700}>Edit HS Code</Typography>
                    <IconButton size="small" onClick={() => setEditTarget(null)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                {editTarget && (
                    <>
                        <DialogContent dividers>
                            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                {editTarget.fullPath}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    label="Code"
                                    value={editTarget.codeWoSection || editTarget.code}
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Description"
                                    value={editForm?.description || ""}
                                    onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                                    <TextField
                                        label="Unit of Measure"
                                        value={editForm?.unitOfMeasure || ""}
                                        onChange={(e) => setEditForm((p) => ({ ...p, unitOfMeasure: e.target.value }))}
                                    />
                                    <TextField
                                        label="Duty Rate"
                                        value={editForm?.dutyRate || ""}
                                        onChange={(e) => setEditForm((p) => ({ ...p, dutyRate: e.target.value }))}
                                    />
                                    <TextField
                                        label="Duty Method"
                                        value={editForm?.dutyMethod || ""}
                                        onChange={(e) => setEditForm((p) => ({ ...p, dutyMethod: e.target.value }))}
                                    />
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button variant="outlined" onClick={() => setEditTarget(null)}>
                                Cancel
                            </Button>
                            <Button variant="contained" startIcon={<Save />} onClick={saveEdit} disabled={saving}>
                                {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <ConfirmDialog
                open={!!deleteTarget}
                title="Delete HS Code?"
                message={`Delete code ${deleteTarget?.codeWoSection || deleteTarget?.code}? Any items already assigned this code will keep it, but it won't be matchable for new items.`}
                confirmLabel="Delete"
                confirmColor="error"
                loading={deleting}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </Box>
    );
};

export default HsCodesPanel;